import { useState, useEffect, useCallback, useRef } from 'react';
import {
  getJobDocuments,
  uploadJobDocument,
  deleteJobDocument,
  setJobDocumentVisibility,
  setJobDocumentLabel,
  getDocumentFlags,
  addDocumentFlags,
  removeDocumentFlag,
  type JobDocument,
  type DocumentFlag,
} from '@/lib/api/jobs';
import apiClient from '@/lib/api/client';
import { DocumentViewerModal } from '@/components/common/DocumentViewerModal';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';

interface DocumentsSectionProps {
  jobId: string;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// Per-type badge: color + icon + extension, so the type reads at a glance.
const FILE_BADGES: { exts: string[]; icon: string; cls: string }[] = [
  { exts: ['pdf'], icon: 'picture_as_pdf', cls: 'bg-red-50 text-red-600' },
  { exts: ['doc', 'docx', 'odt', 'rtf'], icon: 'description', cls: 'bg-blue-50 text-blue-600' },
  { exts: ['xls', 'xlsx', 'csv', 'ods'], icon: 'table_chart', cls: 'bg-green-50 text-green-700' },
  { exts: ['mmdl', 'mmdf', 'stl', 'obj', 'glb', 'gltf', '3mf'], icon: 'view_in_ar', cls: 'bg-purple-50 text-purple-600' },
  { exts: ['dwg', 'dxf'], icon: 'architecture', cls: 'bg-indigo-50 text-indigo-600' },
  { exts: ['zip', 'rar', '7z'], icon: 'folder_zip', cls: 'bg-amber-50 text-amber-700' },
  { exts: ['txt', 'log', 'md'], icon: 'article', cls: 'bg-gray-100 text-gray-500' },
];

function FileBadge({ name }: { name: string }) {
  const ext = name.split('.').pop()?.toLowerCase() ?? '';
  const badge = FILE_BADGES.find(b => b.exts.includes(ext))
    ?? { icon: 'draft', cls: 'bg-gray-100 text-gray-500' };
  return (
    <span className={`w-full h-full flex flex-col items-center justify-center leading-none ${badge.cls}`}>
      <span className="material-symbols-outlined text-[18px] leading-none">{badge.icon}</span>
      <span className="text-[7px] font-bold uppercase mt-0.5 tracking-tight">{ext.slice(0, 4) || 'file'}</span>
    </span>
  );
}

function isImage(type: string, name: string): boolean {
  const ext = name.split('.').pop()?.toLowerCase() ?? '';
  return type.startsWith('image/') || ['jpg','jpeg','png','gif','webp','bmp','tiff','heic','heif'].includes(ext);
}

const ACCEPTED = 'image/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.heic,.heif,.mmdl,.mmdf,.stl,.obj,.glb,.gltf,.3mf';

// ── Flag-for-workers modal ───────────────────────────────────────────────────
// Pick workers on the job crew → they get a notification and the doc pins to
// their portal home ("review required") until each marks it read.
interface TeamMember { user_id: number | null; username?: string; employee_name?: string }
function FlagWorkersModal({ jobId, doc, onClose }: { jobId: string; doc: JobDocument; onClose: (changed: boolean) => void }) {
  const [team, setTeam] = useState<TeamMember[]>([]);
  const [flags, setFlags] = useState<DocumentFlag[]>([]);
  const [checked, setChecked] = useState<Set<number>>(new Set());
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    Promise.all([
      apiClient.get(`/jobs/${jobId}/team`).then(r => (r.data.data ?? []) as TeamMember[]).catch(() => [] as TeamMember[]),
      getDocumentFlags(jobId, doc.id).catch(() => [] as DocumentFlag[]),
    ]).then(([t, f]) => {
      setTeam(t.filter(m => m.user_id));
      setFlags(f);
      setChecked(new Set(f.map(x => x.user_id)));
      setLoaded(true);
    });
  }, [jobId, doc.id]);

  const flagByUser = new Map(flags.map(f => [f.user_id, f]));
  // Workers with a portal account on this job's crew, plus anyone already
  // flagged who has since left the crew (so their state stays visible)
  const people: { user_id: number; name: string }[] = [
    ...team.map(m => ({ user_id: m.user_id!, name: m.employee_name || m.username || `User ${m.user_id}` })),
    ...flags.filter(f => !team.some(m => m.user_id === f.user_id)).map(f => ({ user_id: f.user_id, name: f.name })),
  ];

  const toggle = (uid: number) => setChecked(prev => {
    const next = new Set(prev);
    if (next.has(uid)) next.delete(uid); else next.add(uid);
    return next;
  });

  const save = async () => {
    setBusy(true);
    try {
      const existing = new Set(flags.map(f => f.user_id));
      const toAdd = [...checked].filter(uid => !existing.has(uid));
      const toRemove = [...existing].filter(uid => !checked.has(uid));
      if (toAdd.length) await addDocumentFlags(jobId, doc.id, toAdd, note || undefined);
      for (const uid of toRemove) await removeDocumentFlag(jobId, doc.id, uid);
      onClose(true);
    } catch { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => onClose(false)}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-5 space-y-3" onClick={e => e.stopPropagation()}>
        <div>
          <h3 className="font-bold text-gray-900 text-sm flex items-center gap-1.5">
            <span className="material-symbols-outlined text-amber-500 text-lg">flag</span>
            Flag for review
          </h3>
          <p className="text-xs text-gray-500 mt-0.5 truncate">{doc.name}</p>
          <p className="text-[11px] text-gray-400 mt-1">Flagged workers get a notification and this document pins to their portal home until they mark it read.</p>
        </div>
        {!loaded ? (
          <div className="py-6 text-center"><span className="material-symbols-outlined animate-spin text-2xl text-gray-300">progress_activity</span></div>
        ) : people.length === 0 ? (
          <p className="text-xs text-gray-400 py-3">No workers with portal accounts are on this job's crew yet — add them under the job's Crew section first.</p>
        ) : (
          <div className="max-h-56 overflow-y-auto divide-y divide-gray-50 border border-gray-100 rounded-xl">
            {people.map(pr => {
              const f = flagByUser.get(pr.user_id);
              return (
                <label key={pr.user_id} className="flex items-center gap-2.5 px-3 py-2 cursor-pointer active:bg-gray-50">
                  <input type="checkbox" checked={checked.has(pr.user_id)} onChange={() => toggle(pr.user_id)} className="accent-[#C77D3F]" />
                  <span className="text-sm text-gray-800 flex-1 truncate">{pr.name}</span>
                  {f && (
                    f.read_at
                      ? <span className="text-[10px] font-semibold text-green-700 bg-green-50 border border-green-200 rounded-full px-1.5 py-0.5">✓ read</span>
                      : <span className="text-[10px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-1.5 py-0.5">pending</span>
                  )}
                </label>
              );
            })}
          </div>
        )}
        <input
          value={note} onChange={e => setNote(e.target.value)} maxLength={500}
          placeholder="Note for new flags (optional) — e.g. rev C, check beam sizes"
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#C77D3F]/40"
        />
        <div className="flex gap-2">
          <button onClick={() => onClose(false)} className="flex-1 h-10 rounded-lg border border-gray-300 text-sm font-semibold text-gray-600">Cancel</button>
          <button onClick={save} disabled={busy || !loaded}
            className="flex-1 h-10 rounded-lg bg-[#C77D3F] text-white text-sm font-semibold disabled:opacity-50">
            {busy ? 'Saving…' : 'Save flags'}
          </button>
        </div>
      </div>
    </div>
  );
}

export const DocumentsSection = ({ jobId }: DocumentsSectionProps) => {
  const [documents, setDocuments] = useState<JobDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [viewerDoc, setViewerDoc] = useState<JobDocument | null>(null);
  const [confirmPending, setConfirmPending] = useState<{ message: string; onConfirm: () => void } | null>(null);
  const [flagDoc, setFlagDoc] = useState<JobDocument | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editValue, setEditValue] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const scanInputRef = useRef<HTMLInputElement>(null);

  // Working-name edit happens inline: the name swaps for a text field in place.
  const startEdit = (doc: JobDocument) => {
    setEditingId(doc.id);
    setEditValue(doc.label || doc.name);
  };
  const saveEdit = async (doc: JobDocument) => {
    if (editingId !== doc.id) return;
    setEditingId(null);
    const next = editValue.trim() || null;
    if ((next || '') === (doc.label || '')) return; // unchanged
    setDocuments(ds => ds.map(d => d.id === doc.id ? { ...d, label: next, name: next || d.originalName || d.name } : d));
    try { await setJobDocumentLabel(jobId, doc.id, next); }
    catch { fetchDocuments(); }
  };

  const fetchDocuments = useCallback(async () => {
    try {
      const docs = await getJobDocuments(jobId);
      setDocuments(docs);
    } catch {
      console.error('Failed to fetch documents');
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  useEffect(() => { fetchDocuments(); }, [fetchDocuments]);

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(e.type === 'dragenter' || e.type === 'dragover');
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files?.length) handleFiles(e.dataTransfer.files);
  };

  const handleFiles = async (files: FileList) => {
    setUploading(true);
    setError(null);
    try {
      for (let i = 0; i < files.length; i++) {
        await uploadJobDocument(jobId, files[i]);
      }
      await fetchDocuments();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // "Scan" = the device's own capture UI: phones open the camera (capture hint),
  // desktops fall back to a file picker. Works in every browser — no server-side
  // scanner hardware involved.
  const handleScanPicked = (files: FileList | null) => {
    if (files?.length) handleFiles(files);
    if (scanInputRef.current) scanInputRef.current.value = '';
  };

  const handleDelete = (docId: number) => {
    setConfirmPending({
      message: 'Delete this document?',
      onConfirm: async () => {
        setConfirmPending(null);
        setError(null);
        try {
          await deleteJobDocument(jobId, String(docId));
          setDocuments(prev => prev.filter(d => d.id !== docId));
        } catch {
          setError('Failed to delete document');
        }
      },
    });
  };

  const handleDownload = async (doc: { id: number; name: string }) => {
    try {
      const res = await apiClient.get(`/jobs/${jobId}/documents/${doc.id}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(res.data as Blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.name;
      document.body.appendChild(a);
      a.click();
      URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch {
      setError('Failed to download document');
    }
  };

  if (loading) {
    return (
      <div className="bg-white p-4 rounded-xl border border-gray-200 flex justify-center py-8">
        <span className="material-symbols-outlined animate-spin text-3xl text-gray-400">progress_activity</span>
      </div>
    );
  }

  return (
    <>
      <div>
        <div className="bg-white p-3.5 rounded-xl border border-gray-200">
          {error && (
            <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm flex items-center gap-2">
              <span className="material-symbols-outlined text-lg">error</span>
              {error}
              <button onClick={() => setError(null)} className="ml-auto">
                <span className="material-symbols-outlined text-lg">close</span>
              </button>
            </div>
          )}

          <div className="flex gap-3">
            <label
              htmlFor="job-dropzone-file"
              className={`flex flex-col items-center justify-center flex-1 h-24 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
                dragActive ? 'border-primary bg-primary/10' : 'border-gray-200 bg-gray-50 hover:bg-gray-100'
              } ${uploading ? 'pointer-events-none opacity-60' : ''}`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              <div className="flex flex-col items-center justify-center py-3">
                {uploading ? (
                  <>
                    <span className="material-symbols-outlined animate-spin text-4xl text-primary">progress_activity</span>
                    <p className="mt-2 text-sm text-gray-600">Uploading...</p>
                  </>
                ) : (
                  <>
                    <span className="material-symbols-outlined text-3xl text-gray-500">cloud_upload</span>
                    <p className="text-xs text-gray-600">
                      <span className="font-semibold">Click to upload</span> or drag and drop
                    </p>
                    <p className="text-xs text-gray-500">Images, PDF, Word, Excel (max 25 MB)</p>
                  </>
                )}
              </div>
              <input
                ref={fileInputRef}
                id="job-dropzone-file"
                type="file"
                className="hidden"
                multiple
                accept={ACCEPTED}
                onChange={e => { if (e.target.files?.length) handleFiles(e.target.files); }}
              />
            </label>

            <button
              onClick={() => scanInputRef.current?.click()}
              disabled={uploading}
              className={`flex flex-col items-center justify-center w-28 h-24 border-2 border-dashed rounded-lg transition-colors ${
                uploading ? 'border-primary bg-primary/10 cursor-wait' : 'border-gray-200 bg-gray-50 hover:bg-gray-100 cursor-pointer'
              }`}
              title="Opens the camera on phones/tablets; a file picker on desktop"
            >
              <span className="material-symbols-outlined text-3xl text-gray-500">document_scanner</span>
              <p className="mt-2 text-sm font-semibold text-gray-600">Scan Document</p>
              <p className="text-[10px] text-gray-400">camera or file</p>
            </button>
            <input
              ref={scanInputRef}
              type="file"
              className="hidden"
              multiple
              accept="image/*,.pdf,.heic,.heif"
              capture="environment"
              onChange={e => handleScanPicked(e.target.files)}
            />
          </div>

          {/* Full-bleed on phones: the table claims the whole viewport width and
              scrolls inside itself; back to a rounded card on md+ */}
          {documents.length > 0 ? (
            <div className="mt-4 overflow-x-auto border-gray-100 max-md:w-screen max-md:relative max-md:left-1/2 max-md:-translate-x-1/2 max-md:border-y md:border md:rounded-xl">
              <table className="w-full text-sm min-w-[640px]">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-wide text-gray-400 border-b border-gray-100 bg-gray-50/60">
                    <th className="px-3 py-2 font-semibold">Document</th>
                    <th className="px-2 py-2 font-semibold whitespace-nowrap">Uploaded</th>
                    <th className="px-2 py-2 font-semibold">Size</th>
                    <th className="px-2 py-2 font-semibold">Visibility</th>
                    <th className="px-2 py-2 font-semibold">Review</th>
                    <th className="px-2 py-2 font-semibold text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {documents.map(doc => (
                    <tr key={doc.id} className="hover:bg-gray-50 transition-colors">
                      {/* Document: thumb + working name + original filename */}
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-2.5 min-w-0">
                          <button
                            onClick={() => setViewerDoc(doc)}
                            className="h-9 w-9 rounded-md overflow-hidden bg-gray-100 flex items-center justify-center flex-shrink-0"
                            title="View"
                          >
                            {isImage(doc.type, doc.name) ? (
                              <img src={doc.url} alt={doc.name} className="w-full h-full object-cover" />
                            ) : (
                              <FileBadge name={doc.originalName || doc.name} />
                            )}
                          </button>
                          <div className="min-w-0">
                            {editingId === doc.id ? (
                              <input
                                autoFocus
                                value={editValue}
                                onChange={e => setEditValue(e.target.value)}
                                onBlur={() => saveEdit(doc)}
                                onKeyDown={e => {
                                  if (e.key === 'Enter') saveEdit(doc);
                                  if (e.key === 'Escape') setEditingId(null);
                                }}
                                placeholder="Working name (blank keeps original)"
                                className="w-48 border border-[#C77D3F] rounded-md px-2 py-1 text-sm font-medium text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#C77D3F]/30"
                              />
                            ) : (
                              <button
                                onClick={() => startEdit(doc)}
                                className="flex items-center gap-1 max-w-[16rem] group/name text-left"
                                title={`${doc.name} — tap to set a working name`}
                              >
                                <span className="font-medium text-gray-900 truncate">{doc.name}</span>
                                <span className="material-symbols-outlined text-[14px] text-gray-300 group-hover/name:text-[#C77D3F] flex-shrink-0">edit</span>
                              </button>
                            )}
                            {doc.label && doc.originalName && (
                              <p className="text-[10px] text-gray-400 truncate max-w-[16rem]" title={`Original file: ${doc.originalName}`}>{doc.originalName}</p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-2 py-2 text-xs text-gray-500 whitespace-nowrap">
                        {doc.uploadedAt ? new Date(doc.uploadedAt).toLocaleString('en-CA', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—'}
                      </td>
                      <td className="px-2 py-2 text-xs text-gray-500 whitespace-nowrap">
                        {doc.size > 0 ? formatFileSize(doc.size) : '—'}
                      </td>
                      {/* Firewall chips */}
                      <td className="px-2 py-2 whitespace-nowrap">
                        <div className="flex gap-1">
                          <button
                            onClick={async () => {
                              const next = !doc.visibleToEmployee;
                              setDocuments(ds => ds.map(d => d.id === doc.id ? { ...d, visibleToEmployee: next } : d));
                              try { await setJobDocumentVisibility(jobId, doc.id, { visible_to_employee: next }); }
                              catch { setDocuments(ds => ds.map(d => d.id === doc.id ? { ...d, visibleToEmployee: !next } : d)); }
                            }}
                            title={doc.visibleToEmployee ? 'Workers can see this — click to hide' : 'Hidden from workers — click to show'}
                            className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full border ${
                              doc.visibleToEmployee
                                ? 'bg-blue-50 text-blue-700 border-blue-200'
                                : 'bg-gray-100 text-gray-400 border-gray-200 line-through'
                            }`}
                          >
                            Workers
                          </button>
                          <button
                            onClick={async () => {
                              const next = !doc.visibleToClient;
                              setDocuments(ds => ds.map(d => d.id === doc.id ? { ...d, visibleToClient: next } : d));
                              try { await setJobDocumentVisibility(jobId, doc.id, { visible_to_client: next }); }
                              catch { setDocuments(ds => ds.map(d => d.id === doc.id ? { ...d, visibleToClient: !next } : d)); }
                            }}
                            title={doc.visibleToClient ? 'Client can see this — click to hide' : 'Hidden from client — click to show'}
                            className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full border ${
                              doc.visibleToClient
                                ? 'bg-amber-50 text-amber-700 border-amber-200'
                                : 'bg-gray-100 text-gray-400 border-gray-200 line-through'
                            }`}
                          >
                            Client
                          </button>
                        </div>
                      </td>
                      {/* Review flag */}
                      <td className="px-2 py-2 whitespace-nowrap">
                        <button
                          onClick={() => setFlagDoc(doc)}
                          title="Flag for specific workers — pins it on their portal home until they mark it read"
                          className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full border inline-flex items-center gap-0.5 ${
                            (doc.flagCount || 0) > 0
                              ? (doc.flagReadCount === doc.flagCount
                                  ? 'bg-green-50 text-green-700 border-green-200'
                                  : 'bg-amber-50 text-amber-700 border-amber-300')
                              : 'bg-gray-100 text-gray-400 border-gray-200'
                          }`}
                        >
                          <span className="material-symbols-outlined text-[11px] leading-none">flag</span>
                          {(doc.flagCount || 0) > 0 ? `${doc.flagReadCount}/${doc.flagCount} read` : 'Flag'}
                        </button>
                      </td>
                      {/* Actions — always visible */}
                      <td className="px-2 py-2">
                        <div className="flex items-center justify-end gap-0.5">
                          <button onClick={() => setViewerDoc(doc)} title="View"
                            className="h-8 w-8 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 flex items-center justify-center">
                            <span className="material-symbols-outlined text-lg">visibility</span>
                          </button>
                          <button onClick={() => handleDownload(doc)} title="Download"
                            className="h-8 w-8 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 flex items-center justify-center">
                            <span className="material-symbols-outlined text-lg">download</span>
                          </button>
                          <button onClick={() => handleDelete(doc.id)} title="Delete"
                            className="h-8 w-8 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 flex items-center justify-center">
                            <span className="material-symbols-outlined text-lg">delete</span>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="mt-4 text-center py-8 text-gray-400">
              <span className="material-symbols-outlined text-4xl mb-2 block opacity-50">photo_library</span>
              <p className="text-sm">No documents uploaded yet</p>
              <p className="text-xs mt-1">Upload plans, permits, or photos to get started</p>
            </div>
          )}
        </div>
      </div>

      <DocumentViewerModal
        doc={viewerDoc}
        onClose={() => setViewerDoc(null)}
        onDownload={handleDownload}
      />
      {flagDoc && (
        <FlagWorkersModal
          jobId={jobId}
          doc={flagDoc}
          onClose={changed => { setFlagDoc(null); if (changed) fetchDocuments(); }}
        />
      )}
      {confirmPending && (
        <ConfirmDialog
          message={confirmPending.message}
          onConfirm={confirmPending.onConfirm}
          onCancel={() => setConfirmPending(null)}
        />
      )}
    </>
  );
};
